// Supervisor sign-in for the console, on top of supabase-js Auth.
//
// supabase-js owns the GoTrue password grant, token refresh, AND session persistence
// (persistSession + autoRefreshToken are configured in ../lib/supabase-client.ts) — so the
// "stay-logged-in" behavior the pilot console hand-rolled is library-native here. This module
// keeps only the thin policy on top:
//   - SAFE_SIGN_IN_FAILURE discipline: ALL failure paths (bad creds, transport, malformed) return
//     ONE fixed generic message. The underlying GoTrue/AuthError is NEVER surfaced — it can
//     distinguish "no such user" from "bad password" and may echo the email.
//   - Pre-network input hygiene (validateSupervisorCredentials) for inline form hints.
// Mirrors apps/web/src/supervisor-auth.ts's invariants (ported, not the fetch transport).

import type { SupabaseClient } from "@supabase/supabase-js";

/** The single SAFE, generic sign-in failure message — identical across every failure path so the
 *  surface cannot be used to tell "bad password" from "no such user" or to leak anything. */
export const SAFE_SIGN_IN_FAILURE = "Sign-in failed — check your email and password.";
export const SAFE_SIGN_IN_CODE_SENT =
  "Check your email for a sign-in link and 6-digit code if that supervisor account exists.";
export const SAFE_SIGN_IN_CODE_FAILURE = "That code did not work. Check the latest email and try again.";
export const SAFE_PASSWORD_RESET_SENT =
  "If that supervisor account exists, we sent a password reset link.";
export const SAFE_PASSWORD_UPDATE_FAILURE = "Password reset failed. Request a fresh reset link and try again.";

// Lenient email shape: a single @ with non-empty, space-free local/domain parts and a dotted
// domain. Input hygiene only — Supabase GoTrue is the real arbiter.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface Credentials {
  email: string;
  password: string;
}

export type ValidateCredentialsResult = { ok: true } | { ok: false; reason: string };

/**
 * Validate inputs BEFORE any network call: a non-empty, plausibly-shaped email and a non-empty
 * password. The failure `reason` names the offending field but NEVER includes the value — safe to
 * show inline in the form.
 */
export function validateSupervisorCredentials(input: Credentials): ValidateCredentialsResult {
  if (typeof input.email !== "string" || input.email.trim().length === 0) {
    return { ok: false, reason: "Email is required." };
  }
  if (!EMAIL_PATTERN.test(input.email.trim())) {
    return { ok: false, reason: "Email is not a valid address." };
  }
  // Presence only — never logged or echoed; the trim is an emptiness test, not a transform.
  if (typeof input.password !== "string" || input.password.trim().length === 0) {
    return { ok: false, reason: "Password is required." };
  }
  return { ok: true };
}

export type SignInResult = { ok: true } | { ok: false; reason: string };
export type SafeMessageResult = { ok: true; message: string } | { ok: false; reason: string };

/**
 * Sign a supervisor in via supabase-js (`signInWithPassword`). On success supabase-js persists +
 * auto-refreshes the session; the AuthProvider's onAuthStateChange flips the UI to the shell.
 * On ANY failure returns { ok:false, reason: SAFE_SIGN_IN_FAILURE } — the AuthError is never read.
 */
export async function signInWithSupabase(
  client: SupabaseClient,
  credentials: Credentials,
): Promise<SignInResult> {
  const validation = validateSupervisorCredentials(credentials);
  if (!validation.ok) {
    // The form uses validateSupervisorCredentials directly for field hints; the sign-in boundary
    // itself still collapses to the generic message.
    return { ok: false, reason: SAFE_SIGN_IN_FAILURE };
  }
  try {
    const { data, error } = await client.auth.signInWithPassword({
      email: credentials.email.trim(),
      password: credentials.password,
    });
    if (error !== null || data.session === null) {
      return { ok: false, reason: SAFE_SIGN_IN_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_SIGN_IN_FAILURE };
  }
}

export type ValidateEmailResult = { ok: true } | { ok: false; reason: string };

export function validateSupervisorEmail(email: string): ValidateEmailResult {
  if (typeof email !== "string" || email.trim().length === 0) {
    return { ok: false, reason: "Email is required." };
  }
  if (!EMAIL_PATTERN.test(email.trim())) {
    return { ok: false, reason: "Email is not a valid address." };
  }
  return { ok: true };
}

export function validateEmailCode(code: string): ValidateEmailResult {
  if (typeof code !== "string" || !/^\d{6}$/.test(code.trim())) {
    return { ok: false, reason: "Enter the 6-digit code." };
  }
  return { ok: true };
}

export function validateNewPassword(password: string): ValidateEmailResult {
  if (typeof password !== "string" || password.length < 8) {
    return { ok: false, reason: "Password must be at least 8 characters." };
  }
  return { ok: true };
}

/**
 * Ask Supabase to email a supervisor sign-in link/code. The response is intentionally
 * non-enumerating: bad email, missing account, or transport/auth errors all get the same safe
 * "check email if it exists" success copy after the request boundary.
 *
 * Supabase renders the actual email as a magic link or a code based on the project email
 * template: `{{ .ConfirmationURL }}` gives a link, `{{ .Token }}` gives the OTP code.
 */
export async function requestSupervisorEmailCode(
  client: SupabaseClient,
  email: string,
  redirectTo: string,
): Promise<SafeMessageResult> {
  const validation = validateSupervisorEmail(email);
  if (!validation.ok) {
    return validation;
  }
  try {
    await client.auth.signInWithOtp({
      email: email.trim(),
      options: {
        shouldCreateUser: false,
        emailRedirectTo: redirectTo,
      },
    });
  } catch {
    // Non-enumerating by design.
  }
  return { ok: true, message: SAFE_SIGN_IN_CODE_SENT };
}

export async function verifySupervisorEmailCode(
  client: SupabaseClient,
  input: { email: string; code: string },
): Promise<SignInResult> {
  const emailValidation = validateSupervisorEmail(input.email);
  if (!emailValidation.ok) {
    return { ok: false, reason: emailValidation.reason };
  }
  const codeValidation = validateEmailCode(input.code);
  if (!codeValidation.ok) {
    return { ok: false, reason: codeValidation.reason };
  }
  try {
    const { data, error } = await client.auth.verifyOtp({
      email: input.email.trim(),
      token: input.code.trim(),
      type: "email",
    });
    if (error !== null || data.session === null) {
      return { ok: false, reason: SAFE_SIGN_IN_CODE_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_SIGN_IN_CODE_FAILURE };
  }
}

export async function requestPasswordReset(
  client: SupabaseClient,
  email: string,
  redirectTo: string,
): Promise<SafeMessageResult> {
  const validation = validateSupervisorEmail(email);
  if (!validation.ok) {
    return validation;
  }
  try {
    await client.auth.resetPasswordForEmail(email.trim(), { redirectTo });
  } catch {
    // Non-enumerating by design.
  }
  return { ok: true, message: SAFE_PASSWORD_RESET_SENT };
}

export async function updateRecoveredPassword(
  client: SupabaseClient,
  password: string,
): Promise<SignInResult> {
  const validation = validateNewPassword(password);
  if (!validation.ok) {
    return { ok: false, reason: validation.reason };
  }
  try {
    const { data, error } = await client.auth.updateUser({ password });
    if (error !== null || data.user === null) {
      return { ok: false, reason: SAFE_PASSWORD_UPDATE_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_PASSWORD_UPDATE_FAILURE };
  }
}

/** Sign out (best-effort). onAuthStateChange drives the UI back to unauthenticated regardless. */
export async function signOutSupervisor(client: SupabaseClient): Promise<void> {
  try {
    await client.auth.signOut();
  } catch {
    // Never surface a sign-out error; the local session clear still unwinds the UI.
  }
}
